<template>
  <div class="effective-date-time-picker form-control d-flex">
    <b-dropdown
      class="flex-fill"
      no-caret
      :disabled="disabled"
      :variant="variant"
      :no-flip="true"
      :popper-opts="{
        positionFixed: true
      }"
      size="sm"
    >
      <template slot="button-content">
        <div
          class="d-flex align-items-center justify-content-between"
          style="font-size: 14px"
        >
          <div>
            <span class="mr-1 text-primary">
              Effective:
            </span>
            <span>{{ label }}</span>
          </div>
          <i style="font-size:23px" class="fa fa-angle-down"></i>
        </div>
      </template>
      <b-dropdown-form class="px-2">
        <b-form-group>
          <b-calendar
            v-model="date"
            :readonly="readonly"
            :width="'230px'"
            :min="minDate"
            :max="maxDate"
            :date-disabled-fn="dateDisabledFn"
            hide-header
          />
        </b-form-group>
        <b-form-group
          class="d-flex flex-row justify-content-center align-items-center mb-0"
        >
          <div
            v-if="showAllDay"
            class="d-flex justify-content-start align-items-center mb-2"
          >
            <vue-toggles
              @click="allDay = !allDay"
              :value="allDay"
              height="30"
              width="50"
              checkedText=""
              uncheckedText=""
              checkedBg="#34C759"
              uncheckedBg="#FF6161"
            />
            <span class="ml-2">All Day</span>
          </div>
          <b-time
            v-model="time"
            :readonly="readonly || allDay"
            locale="en"
            hide-header
          />
          <div class="mt-2 text-center">
            <b-button
              size="sm"
              variant="outline-danger"
              v-if="showClearButton"
              @click="time = ''"
            >
              Clear time
            </b-button>
          </div>
        </b-form-group>
      </b-dropdown-form>
    </b-dropdown>
  </div>
</template>

<script>
import moment from "moment";
import {
  checkDateFormat,
  formatDate,
  formatDateOnly,
  formatDateOnlyMysql,
  formatTimeOnlyMysql
} from "../../helpers/date";

export default {
  name: "EffectiveDateTimePicker",
  props: {
    value: {
      type: [Date, String],
      default: null
    },
    placeholder: {
      type: String,
      default: "No date selected"
    },
    readonly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    errors: {
      type: Array,
      default: () => []
    },
    minDate: {
      type: [Date, String],
      default: null
    },
    maxDate: {
      type: [Date, String],
      default: null
    },
    dateDisabledFn: {
      type: Function,
      default: null
    },
    showAllDay: {
      type: Boolean,
      default: false
    },
    beginTime: {
      type: String,
      default: "00:00"
    },
    endTime: {
      type: String,
      default: "23:59"
    }
  },
  methods: {
    emit(date, time, allDay) {
      if (date) {
        if (allDay) {
          this.$emit("input", `${date} allDay`);
        } else if (time) {
          this.$emit("input", `${date} ${time}`);
        } else {
          this.$emit("input", `${date} ${this.beginTime}`);
        }
      }
    }
  },
  computed: {
    date: {
      get() {
        return this.value ? formatDateOnlyMysql(this.value.split(" ")[0]) : "";
      },
      set(newValue) {
        this.emit(newValue, this.time, this.allDay);
      }
    },
    time: {
      get() {
        if (this.value) {
          if (checkDateFormat(this.value, "YYYY-MM-DD HH:mm:ss")) {
            return formatTimeOnlyMysql(this.value);
          } else {
            return formatTimeOnlyMysql(this.value + ":00");
          }
        } else return "";
      },
      set(newValue) {
        if (!newValue) {
          this.emit(this.date, "", this.allDay);
          return;
        }

        const time = moment(newValue.slice(0, 5), "HH:mm");

        if (
          !this.beginTime ||
          this.beginTime == "00:00" ||
          !this.endTime ||
          this.endTime == "23:59"
        ) {
          this.emit(this.date, time.format("HH:mm:ss"), this.allDay);
          return;
        }

        const beginTime = moment(this.beginTime, "HH:mm");
        const endTime = moment(this.endTime, "HH:mm");

        if (time.isBefore(beginTime)) {
          const diff = beginTime.diff(time);

          if (diff < 3600000 && endTime.minutes() === 0) {
            endTime.subtract(diff);
          }

          this.emit(this.date, endTime.format("HH:mm:ss"), this.allDay);
        } else if (time.isAfter(endTime)) {
          const diff = endTime.diff(time);

          if (diff > -3600000 && beginTime.minutes() === 0) {
            beginTime.subtract(diff);
          }

          this.emit(this.date, beginTime.format("HH:mm:ss"), this.allDay);
        } else {
          this.emit(this.date, time.format("HH:mm:ss"), this.allDay);
        }
      }
    },
    allDay: {
      get() {
        return this.value && this.value.indexOf("allDay") >= 0 ? true : false;
      },
      set(newValue) {
        if (newValue) {
          this.timeSnapshot = this.time;
          this.emit(this.date, "", true);
        } else {
          this.emit(this.date, this.timeSnapshot, false);
        }
      }
    },
    label() {
      if (this.date && this.allDay) {
        return formatDateOnly(this.date) + " All day";
      } else if (this.date && this.time) {
        return formatDate(this.date + " " + this.time);
      } else {
        return this.placeholder;
      }
    },
    variant() {
      return this.errors.length ? "outline-danger" : "text";
    },
    showClearButton() {
      if (this.allDay) {
        return false;
      } else {
        return !!this.time;
      }
    }
  }
};
</script>
<style scoped>
.divider {
  border-right: 1px solid #d8d8d8;
  width: 1px;
  height: 36px;
  position: absolute;
  left: 39px;
  top: -1px;
}
.effective-date-time-picker >>> button {
  height: 33px;
  padding-top: 4px;
}
</style>

<style>
.effective-date-time-picker.form-control {
  padding: 0;
  height: initial;
}

.effective-date-time-picker .dropdown-toggle {
  padding: 0.375rem 0.75rem;
}
</style>
